import { useRef, useState, useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import MapPropertiesService from '../services/MapPropertiesService';
import { IncidentPilotApiRepository } from 'repositories/api/IncidentApiRepository.Pilot';
import { isArrayEmpty, isNullOrEmpty } from 'components/helpers/ObjectHelpers';
import { sleep } from '../../../helpers/DataHelpers';
import { MapMarker } from './elements/MapMarker';
import { useMapBounds, useMapZoom, useMapResize, useMapDragEnd } from '../hooks/useMapStore';

interface MapCertificateLocationsProps {
    incidentPilotId: number,
    isActive: boolean,
}

export const MapCertificateLocations = ({
    incidentPilotId,
    isActive,
}: MapCertificateLocationsProps) => {

    const stateBounds = useMapBounds();
    const stateZoom = useMapZoom();
    const stateDragEnd = useMapDragEnd();
    const stateResize = useMapResize();

    const map = useMap();
    const markerRefs: { current: any[] } = useRef([]);

    const maxCount = 15;
    const mapMarkerTypes = MapPropertiesService.markerTypes;
    const locationCertificateTypes = MapPropertiesService.locationCertificateStatusTypes;
    const mapProperties = MapPropertiesService.properties;

    const [certificateLocations, setCertificateLocations] = useState<any[]>([]);
    const [currentZoom, setCurrentZoom] = useState(0);
    const [markers, setMarkers] = useState<any[]>([]);
    const [newMarkers, setNewMarkers] = useState<any[]>([]);

    const componentRef: {
        current: {
            hasInitialized: boolean,
            isBusy: boolean,
            hasCancelled: boolean,
        }
    } = useRef({
        hasInitialized: false,
        isBusy: false,
        hasCancelled: false
    });
    const { current: localRef } = componentRef;

    const handleChanges = () => {
        localRef.hasCancelled = true;
        getLocations();
    }

    useEffect(() => {
        handleChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateBounds, stateZoom, stateDragEnd, stateResize]);

    useEffect(() => {
        return (() => {
            localRef.hasInitialized = false;
            localRef.isBusy = false;

            setMarkers([]);
            setCertificateLocations([]);
        })
    }, []);

    useEffect(() => {
        if (!map) return;
        if (!isActive) {
            setMarkers([]);
            setCertificateLocations([]);
        } else {
            initialize();
        }
    }, [map, isActive]);

    useEffect(() => {
        getLocations();
    }, [certificateLocations]);

    useEffect(() => {
        if (isArrayEmpty(newMarkers)) return;
        for (const marker of newMarkers) {
            markers.push(marker);
        }
    }, [newMarkers]);

    return (
        markers.map((marker: any, index: number) =>
            <MapMarker
                ref={(e: any) => markerRefs.current[index] = e}
                key={index}
                marker={marker}
                currentZoom={currentZoom}
            />
        )
    );

    async function initialize() {
        const response = await IncidentPilotApiRepository.getCertificateLocationsAsync(incidentPilotId);
        if (response.ok) {
            const data = response instanceof Response ? await response.json() : [];
            setCertificateLocations(data);
        }
    }

    async function getLocations() {
        if (!map) return;

        const zoom = map.getZoom();

        for (const ref of markerRefs.current) {
            if (isNullOrEmpty(ref)) continue;
            ref.onToggleVisibility(zoom);
        }

        if (zoom && zoom < mapProperties.MarkersVisibleAtZoom) return;

        localRef.isBusy = true;

        const bounds = map.getBounds();
        if (!bounds) return;

        localRef.hasCancelled = false;

        let dataMarkers: any[] = [];
        for (const location of certificateLocations) {
            if (localRef.hasCancelled) break;

            const existing = markers.filter(m => m.id === location.locationId);
            if (isArrayEmpty(existing)) {
                const latLng = new window.google.maps.LatLng(location.latitude, location.longitude);
                const locationBounds = new google.maps.LatLngBounds();
                locationBounds.extend(latLng);

                if (!bounds.intersects(locationBounds)) continue;
                const marker = {
                    id: location.locationId,
                    description: location.locationName,
                    lastPilotageDate: location.lastPilotageDate,
                    certificateStatus: location.locationCertificateStatusName,
                    position: latLng,
                    type: mapMarkerTypes.CertificateLocation,
                    color: getLocationColor(location.locationCertificateStatusSystemName),
                    currentZoom: zoom,
                    topmost: true,
                };

                dataMarkers.push(marker);

                if (dataMarkers.length % maxCount === 0) {
                    setNewMarkers(dataMarkers);
                    dataMarkers = [];
                    await sleep(5);
                }
            }
        }

        if (localRef.hasCancelled) return;
        setNewMarkers(dataMarkers);

        localRef.hasInitialized = true;
        localRef.isBusy = false;

        onSetZoom();
    }

    function getLocationColor(systemName: string) {
        switch (systemName) {
            case locationCertificateTypes.HasCertificate:
                return "var(--ppcls-hc)";
            case locationCertificateTypes.HasCertificateNeedTestRun:
                return "var(--ppcls-hcntr)";
            case locationCertificateTypes.CertificateFrozen:
                return "var(--ppcls-cf)";
            case locationCertificateTypes.KnowAreaSailAsLocalExpert:
                return "var(--ppcls-kamsale)";
            case locationCertificateTypes.HasNoCertificate:
                return "";
            case locationCertificateTypes.CertificateExpired:
                return "var(--ppcls-ce)";
            case locationCertificateTypes.ApprovedExpansionNeedTestRun:
                return "var(--ppcls-aentr)";
        }   
    }

    function onSetZoom() {
        if (!map) return;

        const zoom = map.getZoom();
        if (zoom)
            setCurrentZoom(zoom);
    }

}

